import { memo, useCallback } from 'react';

import { ChevronRightIcon } from '@air/design-icons';

import { WorkspaceAgentPillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillLeadingVisual';
import { WORKSPACE_AGENT_PANEL_ASSET_LIST_ROW_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';
import { getWorkspaceAgentAssetListRowMetaLabel } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentAssetListRowMetaLabel';

export type WorkspaceAgentAssetListRowProps = {
  onOpen?: (id: string) => void;
  thumbnail: WorkspaceAgentThumbnail;
};

export const WorkspaceAgentAssetListRow = memo(({ onOpen, thumbnail }: WorkspaceAgentAssetListRowProps) => {
  const metaLabel = getWorkspaceAgentAssetListRowMetaLabel({
    ext: thumbnail.ext,
    height: thumbnail.height,
    width: thumbnail.width,
  });
  const isOpenable = Boolean(onOpen) && thumbnail.canOpen !== false && !thumbnail.isUnavailable;

  const handleOpen = useCallback(() => {
    onOpen?.(thumbnail.id);
  }, [onOpen, thumbnail.id]);

  const leading = (
    <span className="relative size-7 shrink-0 overflow-hidden rounded-4 ads:bg-ads-gray-4 bg-gray-4">
      <WorkspaceAgentPillLeadingVisual
        assetType={thumbnail.type ?? null}
        ext={thumbnail.ext ?? null}
        thumbnailDimensions={WORKSPACE_AGENT_PANEL_ASSET_LIST_ROW_THUMBNAIL}
        thumbnailLayout="fill"
        thumbnailUrl={thumbnail.thumbnailUrl}
      />
    </span>
  );

  const body = (
    <>
      {leading}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium ads:text-primary text-12 text-gray-12">{thumbnail.label}</span>
        {metaLabel ? <span className="block truncate ads:text-metadata text-12 text-gray-10">{metaLabel}</span> : null}
      </span>
      {isOpenable ? (
        <ChevronRightIcon
          aria-hidden
          className="size-4 shrink-0 ads:text-icon-subtle text-gray-9 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
          size="sm"
        />
      ) : null}
    </>
  );

  if (isOpenable) {
    return (
      <button
        aria-label={`Open asset from ${thumbnail.label}`}
        className="group/row flex w-full items-center gap-2 rounded-4 px-1 py-1 text-left transition-colors ads:hover:bg-hover-subtle hover:bg-gray-3 ads:focus-visible:bg-hover-subtle focus-visible:bg-gray-3 focus-visible:outline-none"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_ROW"
        data-thumbnail-id={thumbnail.id}
        onClick={handleOpen}
        type="button"
      >
        {body}
      </button>
    );
  }

  return (
    <div
      className="flex w-full items-center gap-2 rounded-4 px-1 py-1"
      data-testid="WORKSPACE_AGENT_ASSET_LIST_ROW"
      data-thumbnail-id={thumbnail.id}
    >
      {body}
    </div>
  );
});

WorkspaceAgentAssetListRow.displayName = 'WorkspaceAgentAssetListRow';
